import {
    Component,
    computed,
    effect,
    Injector,
    OnInit,
    QueryList,
    signal,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, ResolveFn, Router } from '@angular/router';
import { removeNullValuesFromGendersCollection } from '@app/components/add-genders/functions/remove-null-genders';
import { removeNullValuesFromPronounsCollection } from '@app/components/add-pronouns/functions/remove-null-pronouns';
import { SHORT_DATE_FORMAT } from '@app/constants';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { WarningsToDisplayProvider } from '@app/interfaces/warnings-to-display-provider.interface';
import { Address } from '@app/models/employee/address.model';
import { DataFieldGroup } from '@app/models/settings/data-field-group.model';
import { CUSTOM_DATA_GROUP_DIALOG } from '@app/modules/common/views/show/show.view';
import { DataFieldGroupValuesComponent } from '@app/modules/employees/components/data-field-group-values/data-field-group-values.component';
import { DataFieldGroupsComponent } from '@app/modules/employees/components/data-field-groups/data-field-groups.component';
import { PersonalShowWarningProvider } from '@app/modules/employees/warning-providers/personal-show.warning-provider';
import { BaseForm } from '@app/modules/forms/base.form';
import { AbilityService, AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { Locale } from '@app/types/translatable.type';
import { ChangeRequestTemplateDisplayOptions } from '@app/types/translatables/change-request-template-display.options';
import { ShowView } from '@common/views';
import { EmployeesResolver } from '@employees/employees.resolver';
import { getDataFieldGroupByName } from '@employees/functions/get-data-field-group-by-name';
import { EmployeeChangeRequestTemplatesService } from '@employees/services';
import { BasicInformationForm, PersonalInformationForm } from '@forms/employees/components';
import { Account } from '@models/account/account.model';
import { ChangeRequestTemplate } from '@models/change-requests/change-request-template.model';
import { DataValue } from '@models/employee/data-value.model';
import { Employee } from '@models/employee/employee.model';
import { DataField } from '@models/settings/data-field.model';
import { TranslateService } from '@ngx-translate/core';
import { DefaultGroupFieldNamesEnum } from '@settings/enums/default-group-field-names.enum';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

type PersonalShowDialogType =
    | 'BASIC_INFO'
    | 'PERSONAL_INFO'
    | 'ADDRESS'
    | 'EMERGENCY_CONTACTS'
    | CUSTOM_DATA_GROUP_DIALOG;

@Component({
    templateUrl: './personal-show.template.html',
    styleUrls: ['./personal-show.style.scss'],
})
export class PersonalShowView extends ShowView implements OnInit {
    @ViewChild('editFormBasicInformationForm', { static: false }) editFormBasicInformationForm: BasicInformationForm;
    @ViewChild('editFormPersonalInformationForm', { static: false })
    editFormPersonalInformationForm: PersonalInformationForm;
    @ViewChild('editFormEc', { static: false }) editFormEc: NgForm;
    @ViewChildren(DataFieldGroupValuesComponent)
    dataFieldGroupValuesComponents?: QueryList<DataFieldGroupValuesComponent>;
    @ViewChildren(DataFieldGroupsComponent) dataFieldGroupsComponents?: QueryList<DataFieldGroupsComponent>;

    changeRequestTemplate = ChangeRequestTemplateDisplayOptions;
    isSelfEmployeeView = false;

    isLoading = true;

    employee: Employee;
    address: Address;
    account?: Account;
    dataValues: DataValue[];
    dataFields: DataField[] | null = null;
    editableDataFields: DataField[];
    defaultGroupFieldNames = DefaultGroupFieldNamesEnum;
    warningProvider: WarningsToDisplayProvider;

    viewLegalNamePermission: string[] = Account.legalNamePermission.some('view');
    viewLegalSexPermission: string[] = Employee.legalSexPermission.some('view');

    viewAddressPermission: string[] = Address.permission.some('view');
    viewPersonalInformationPermission: string[] = ['view.personalInformation'];
    viewEmergencyContactPermission: string[] = ['view.employeeEmergencyContact'];

    viewSinPermission: string[] = Employee.sinPermission.some('view');
    viewBornOnPermission: string[] = Employee.bornOnPermission.some('view');

    viewPersonalInformationPermissions: string[] = [
        ...this.viewPersonalInformationPermission,
        ...this.viewBornOnPermission,
        ...this.viewSinPermission,
    ];

    editBasicInformationPermission: string[] = Employee.permission.edit;
    editAddressPermission: string[] = Address.permission.edit;
    canEditDataValues = false;
    editPersonalInformationPermission: string[] = ['update.personalInformation'];
    editEmergencyContactPermission: string[] = ['update.employeeEmergencyContact'];
    hasUpcomingTermination = false;

    basicInformationModalDescription = 'employees.personal.basicSectionDescription';

    addPendoTrackingForGenders = false;
    addPendoTrackingForPronouns = false;

    showAddCustomFieldsButton = false;

    canShowEditBasicInformation = false;
    canShowEditPersonalInformation = false;
    canShowEditAddress = false;
    canShowEditEmergencyContact = false;

    hasChangeRequestFeatureFlag = false;
    hasKYCKYBFeatureFlag = signal(false);

    changeRequestTemplates: ChangeRequestTemplate[] = [];
    canCreateChangeRequest = false;
    bannerDate = '';
    showLeaveOfAbsenceBanner = false;
    shouldShowBirthdayVisibility = false;

    getDataFieldGroupByName = getDataFieldGroupByName;

    currentlyOpenDialog?: PersonalShowDialogType = undefined;

    /**
     * Indicates whether an employee being viewed is one that would be required to complete KYC
     * This property determines whether we will show the KYC card when an admin is viewing the current employee.
     * This is different from the permission to view the KYC card on their own profile
     */
    isKYCVisibleForViewedEmployee = signal(false);

    /**
     * Indicates whether the currently logged in user is required to complete KYC
     */
    isKYCVisibleForCurrentUser = computed(() => {
        if (!this.hasKYCKYBFeatureFlag()) {
            return false;
        }
        return this.auth.hasPayrollAccess;
    });

    checkEmployeePermissions = effect(() => {
        // If the current user does not have access to KYC, then they also cannot view it on another employee's profile
        // Similarly, if it's the own user's profile then we don't need to check the employee permissions (they're already in AuthService)
        if (!this.isKYCVisibleForCurrentUser() || this.isUserProfile) {
            return;
        }

        this.auth
            .canEmployee(this.employee, 'accessPayroll')
            .pipe(catchError(() => of(false))) // If the permissions API call fails then we default to not showing the card
            .subscribe(this.isKYCVisibleForViewedEmployee.set);
    });

    protected resolver: {
        resolve: ResolveFn<any>;
    };

    constructor(
        public route: ActivatedRoute,
        public auth: AuthService,
        public changeRequestTemplateService: EmployeeChangeRequestTemplatesService,
        protected router: Router,
        protected abilities: AbilityService,
        protected notify: NotifyService,
        protected injector: Injector,
        protected analyticsService: AnalyticService,
        protected featureService: FeatureService
    ) {
        super();

        this.setResolver();
        this.employee = this.route.snapshot.data.models.get('employee');
        this.hasUpcomingTermination = this.employee.hasUpcomingTermination();
        this.canEditDataValues = this.auth.hasManageAccess(this.employee);

        this.address = this.employee.address ?? new Address();
        this.account = this.employee.account;
        this.setEditBasicInformationPermission();
        this.updateEditModalsVisibilitiesFromBasePermissions();
        this.setWarningProvider();
        this.setDataValues();
        this.setDataFields();
    }

    canUserManageCustomFields(): boolean {
        return this.auth.can(DataField.permission.all) && this.auth.can(DataFieldGroup.permission.all);
    }

    onAddCustomFields(): void {
        this.router.navigate(['/settings', 'custom-fields', 'fields']);
    }

    setAddPendoTrackingForGenders(addPendoTracking: boolean): void {
        this.addPendoTrackingForGenders = addPendoTracking;
    }

    setAddPendoTrackingForPronouns(addPendoTracking: boolean): void {
        this.addPendoTrackingForPronouns = addPendoTracking;
    }

    reResolve(): void {
        this.isLoading = true;
        this.resolver
            .resolve(this.route.snapshot, this.router.routerState.snapshot)
            .then((models) => {
                this.employee = <Employee>models.get('employee');

                this.address = this.employee.address ?? new Address();
                this.account = this.employee.account;
                this.setDataValues(true);
                this.setWarningProvider();
                this.isLoading = false;
            })
            .catch((err) => err.message || 'Unable to reload employee');
    }

    filterNullValuesFromGendersAndPronouns(): void {
        this.employee.genders = removeNullValuesFromGendersCollection(this.employee.genders);
        this.employee.pronouns = removeNullValuesFromPronounsCollection(this.employee.pronouns);
    }

    /**
     * Save Models
     */
    onSave(form?: BaseForm): void {
        let toSave: Promise<any>[] = [];

        if (this.employee && this.employee.isDirty()) {
            toSave.push(this.employee.param('company', this.auth.company.id).save());
        }

        if (this.account && this.account.isDirty()) {
            toSave.push(this.account.param('account', this.employee.accountId).save());
        }

        if (this.address && this.address.isDirty()) {
            toSave.push(
                this.address.param('company', this.employee.companyId).param('employee', this.employee.id).save()
            );
        }

        // To-do: move the save employee name pronunciation here

        toSave = toSave.concat(this.saveDataFields());

        this.isLoading = true;
        Promise.all(toSave)
            .then(() => {
                this.reResolve();
                this.notify.success('saved');
                this.closeDialog();

                // Pendo Events Tracking
                this.addPendoTrackingForGendersAndPronouns();
                this.addPendoTrackingForBasicInformationModal();
                this.addPendoTrackingForPersonalInformationModal();
                this.addPendoTrackingForAddressModal();
                this.addPendoTrackingForEmergencyContactsModal();
            })
            .catch((err) => {
                this.notify.error(err.message || 'couldNotSave');
                this.isLoading = false;
                if (form) {
                    form.addExternalError();
                }
            });
    }

    /**
     * Cancel Model Edit
     */
    onCancel(): void {
        if (this.employee.isDirty()) {
            this.employee.revert();
        }

        if (this.account?.isDirty()) {
            this.account.revert();
        }

        if (this.address.isDirty()) {
            this.address.revert();
        }

        this.editFormBasicInformationForm.onCancel();
        this.closeDialog();
    }

    /**
     * TODO: refactor so this method is not duplicated in job-and-pay-show.view
     */
    saveDataFields(): Promise<any>[] {
        return this.dataValues
            .filter((dataValue: DataValue) => {
                if (dataValue.isDirty()) {
                    return true;
                }

                const dataField = this.dataFields?.find(
                    (currentField: DataField) => Number(currentField.id) === Number(dataValue.dataFieldId)
                );

                if (!dataField) {
                    return false;
                }

                // we are saving a data value that is not dirty
                // because it's required AND hasn't been saved yet
                // PDT-1475
                const shouldPersistPristineValue = dataField.isRequired && !dataValue.isPersisted;
                return shouldPersistPristineValue;
            })
            .map((dataValue) => dataValue.param('company', this.auth.company.id).save());
    }

    onDataFieldsSave(form?: BaseForm): void {
        const dataValuePromises = this.saveDataFields();
        Promise.all(dataValuePromises)
            .then(() => {
                this.reResolve();
                this.notify.success('saved');
                this.closeDialog();

                if (!this.isSelfEmployeeView) {
                    this.addPendoTrackingForCustomFieldsModal();
                }
            })
            .catch((err) => {
                this.notify.error(err.message || 'couldNotSaveData');
                form?.addExternalError();
            });
    }

    async ngOnInit(): Promise<void> {
        const [hasChangeRequestFeatureFlag, shouldShowBirthdayVisibility, hasKYCKYBFeatureFlag] =
            await this.featureService.hasMany(
                FeatureFlag.changeRequests,
                FeatureFlag.birthdayVisibility,
                FeatureFlag.KYC_KYB
            );
        this.hasChangeRequestFeatureFlag = hasChangeRequestFeatureFlag;
        this.shouldShowBirthdayVisibility = shouldShowBirthdayVisibility;
        this.hasKYCKYBFeatureFlag.set(hasKYCKYBFeatureFlag);

        if (this.hasChangeRequestFeatureFlag) {
            this.getChangeRequestTemplates();
        }

        if (!this.employee.onLeave && this.employee.leaveOfAbsences.length > 0) {
            // Want the latest leave of absence
            const currentLeaveOfAbsence = this.employee?.leaveOfAbsences[0];
            this.bannerDate = moment(currentLeaveOfAbsence.start).format(
                SHORT_DATE_FORMAT[this.injector.get(TranslateService).currentLang as Locale]
            );

            // We have updated this condition to only show the banner when an admin or direct manager is looking at the profile
            this.showLeaveOfAbsenceBanner =
                moment(currentLeaveOfAbsence.start).isAfter(moment(), 'day') &&
                (this.auth.isAdmin() || this.auth.supervises(this.employee.id));
        }

        this.showAddCustomFieldsButton = this.canUserManageCustomFields();
        this.isLoading = false;
    }

    /**
     * Is the user currently viewing their own profile.
     * This can be useful since we have two personal-show views and both are able to display the currently logged in user
     */
    get isUserProfile(): boolean {
        return this.employee.id === this.auth.employee.id;
    }

    protected setResolver(): void {
        this.resolver = this.injector.get(EmployeesResolver);
    }

    private async getChangeRequestTemplates(): Promise<void> {
        this.changeRequestTemplates = await this.changeRequestTemplateService.getActiveTemplates();
        this.canCreateChangeRequest =
            this.changeRequestTemplateService.canRequestForEmployee(this.employee) &&
            this.changeRequestTemplates.length > 0;
    }

    private setEditBasicInformationPermission(): void {
        this.editBasicInformationPermission = this.auth.canView(this.employee, 'id')
            ? this.editBasicInformationPermission
            : ['noPermission'];
    }

    private updateEditModalsVisibilitiesFromBasePermissions(): void {
        this.canShowEditBasicInformation = this.auth.can(this.editBasicInformationPermission);
        this.canShowEditPersonalInformation = this.auth.can(this.editPersonalInformationPermission);
        this.canShowEditAddress = this.auth.can(this.editAddressPermission);
        this.canShowEditEmergencyContact = this.auth.can(this.editEmergencyContactPermission);
    }

    private setDataValues(isReset = false): void {
        if (this.auth.can(DataValue.permission.view) || this.isUserProfile) {
            DataValue.param('company', this.auth.company.id)
                .where('employee_id', this.employee.id)
                .all()
                .then(([dataValues]) => {
                    this.dataValues = dataValues;
                    // If the data values are being reset due to a form submission the component needs to be signaled to re-assign values
                    if (isReset) {
                        this.dataFieldGroupsComponents?.forEach((component) => component.resetDataValues());
                        this.dataFieldGroupValuesComponents?.forEach((component) => component.resetDataValues());
                    }
                });
        }
    }

    private setDataFields(): void {
        // Want to get the datafields from the company endpoint and not an include so that they are filtered by permissible
        DataField.param('company', this.auth.employee.companyId)
            .with(['dataFieldGroup', 'dataFieldOptions'])
            .where('employeeIds', [this.employee.id])
            .orderBy('orderBy', 'ASC')
            .all()
            .then(([dataFields]) => {
                this.dataFields = dataFields;
                if (!this.auth.hasManageAccess(this.employee)) {
                    this.editableDataFields = [];
                } else {
                    this.editableDataFields = dataFields.filter((dataField) => dataField.editable);
                }

                this.canEditDataValues = this.auth.hasManageAccess(this.employee) && this.editableDataFields.length > 0;

                this.updateEditModalsVisibilitiesFromEditableDataFields();
            });
    }

    private setWarningProvider(): void {
        this.warningProvider = new PersonalShowWarningProvider(this.employee, this.abilities.payroll());
    }

    private updateEditModalsVisibilitiesFromEditableDataFields(): void {
        this.canShowEditBasicInformation =
            this.canShowEditBasicInformation ||
            this.hasEditableDataFieldsForDefaultGroup(this.defaultGroupFieldNames.basic_information);

        this.canShowEditPersonalInformation =
            this.canShowEditPersonalInformation ||
            this.hasEditableDataFieldsForDefaultGroup(this.defaultGroupFieldNames.personal_information);

        this.canShowEditAddress =
            this.canShowEditAddress || this.hasEditableDataFieldsForDefaultGroup(this.defaultGroupFieldNames.address);

        this.canShowEditEmergencyContact =
            this.canShowEditEmergencyContact ||
            this.hasEditableDataFieldsForDefaultGroup(this.defaultGroupFieldNames.address);
    }

    private hasEditableDataFieldsForDefaultGroup(groupName: string): boolean {
        return (this.editableDataFields ?? []).some((df: DataField) => df.dataFieldGroup.name === groupName);
    }

    private addPendoTrackingForGendersAndPronouns(): void {
        if (this.addPendoTrackingForGenders) {
            this.isSelfEmployeeView
                ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileEditGender)
                : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileEditGender);
        }

        if (this.addPendoTrackingForPronouns) {
            this.isSelfEmployeeView
                ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfileEditPronouns)
                : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileEditPronouns);
        }
    }

    private addPendoTrackingForBasicInformationModal(): void {
        const isBasicInformationFormDirty = this.editFormBasicInformationForm?.form.dirty;

        if (isBasicInformationFormDirty) {
            this.isSelfEmployeeView
                ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfilePersonalTabEditBasicInfo)
                : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfilePersonalTabBasicInformationSave);
        }
    }

    private addPendoTrackingForPersonalInformationModal(): void {
        if (this.account && this.account.isDirty()) {
            this.isSelfEmployeeView
                ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfilePersonalTabEditPersonalInfo)
                : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfilePersonalTabPersonalInformationSave);
        }
    }

    private addPendoTrackingForAddressModal(): void {
        if (this.address && this.address.isDirty()) {
            this.isSelfEmployeeView
                ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfilePersonalTabEditAddress)
                : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfilePersonalTabAddressSave);
        }
    }

    private addPendoTrackingForEmergencyContactsModal(): void {
        const isEmergencyContactFormDirty = this.editFormEc?.form.dirty;

        if (isEmergencyContactFormDirty) {
            this.isSelfEmployeeView
                ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfilePersonalTabEditEmergencyContacts)
                : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfilePersonalTabEmergencyContactsSave);
        }
    }

    private addPendoTrackingForCustomFieldsModal(): void {
        this.isSelfEmployeeView
            ? this.analyticsService.trackEvent(AnalyticEvents.SelfProfilePersonalTabEditCustomField)
            : this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfilePersonalTabCustomFieldSave);
    }
}
